import { useRef } from "react";
import Modal from "react-modal/lib/components/Modal";
import "./style.css";

const VARIANT_STYLES = {
	info: "text-sky-600 bg-sky-50",
	warning: "text-amber-600 bg-amber-50",
	error: "text-red-600 bg-red-50",
	success: "text-emerald-600 bg-emerald-50",
	question: "text-indigo-600 bg-indigo-50",
};

const BUTTON_PRESETS = {
	ok: ["ok"],
	okCancel: ["cancel", "ok"],
	confirmCancel: ["cancel", "confirm"],
};

export default function MessageDialog({
	open,
	title = "Confirmação",
	description = "",
	variant = "question", // info | warning | error | success | question
	buttons = "okCancel", // ok | okCancel | confirmCancel
	autoFocus = "ok", // "ok" | "confirm" | "cancel"
	onOk,
	onConfirm,
	onCancel,
	onClose,
	onAfterOpen,
	overlayClassName = "modal-overlay",
	className = "modal-content",
}) {
	const okRef = useRef(null);
	const confirmRef = useRef(null);
	const cancelRef = useRef(null);

	const buttonList = BUTTON_PRESETS[buttons] || BUTTON_PRESETS.ok;

	const getFocusedKey = () => {
		if (document.activeElement === cancelRef.current) return "cancel";
		if (document.activeElement === okRef.current) return "ok";
		if (document.activeElement === confirmRef.current) return "confirm";
		return null;
	};

	const focusButton = (key) => {
		if (key === "cancel" && cancelRef.current) cancelRef.current.focus();
		if (key === "ok" && okRef.current) okRef.current.focus();
		if (key === "confirm" && confirmRef.current) confirmRef.current.focus();
	};

	// >>> FOCO INICIAL AGORA VEM DAQUI <<<
	const handleAfterOpen = () => {
		focusButton(autoFocus);
		onAfterOpen?.();
	};

	if (!open) return null;

	const handleAction = (action) => {
		if (action === "ok" && onOk) onOk();
		if (action === "confirm" && onConfirm) onConfirm();
		if (action === "cancel" && onCancel) onCancel();
		if (onClose) onClose(action);
	};

	const handleKeyDown = (e) => {
		// ESC -> cancelar (além do ESC padrão do react-modal)
		if (e.key === "Escape") {
			e.preventDefault();
			handleAction("cancel");
			return;
		}

		if (e.key === "Enter" && e.target.tagName !== "BUTTON") {
			e.preventDefault();
			focusButton(buttonList[buttonList.length - 1]);
			return;
		}

		// setas esquerda/direita -> alternar entre botões
		if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
			const current = getFocusedKey();
			const list = buttonList;
			const idx = current ? list.indexOf(current) : -1;

			if (idx === -1) {
				focusButton(list[0]);
			} else {
				const dir = e.key === "ArrowRight" ? 1 : -1;
				const nextIdx = (idx + dir + list.length) % list.length;
				focusButton(list[nextIdx]);
			}

			e.preventDefault();
			return;
		}
	};

	return (
		<Modal
			isOpen={open}
			onRequestClose={() => handleAction("cancel")} // ESC ou clique fora (se allowed)
			onAfterOpen={handleAfterOpen} // <<< garante o foco NO FINAL
			overlayClassName={overlayClassName}
			className={className}
			shouldCloseOnOverlayClick={true} // só fecha pelos botões
		>
			<div
				className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 mx-auto"
				onKeyDown={handleKeyDown}
			>
				{/* header */}
				<div className="flex items-center gap-3 px-5 pt-4">
					<div
						className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
							VARIANT_STYLES[variant] || VARIANT_STYLES.question
						}`}
					>
						{variant === "error" && "!"}
						{variant === "warning" && "!"}
						{variant === "info" && "i"}
						{variant === "success" && "✓"}
						{variant === "question" && "?"}
					</div>
					<h2 className="text-base font-semibold text-slate-900">{title}</h2>
				</div>

				{/* content */}
				{description && <div className="px-5 pt-3 pb-4 text-sm text-slate-700">{description}</div>}

				{/* actions */}
				<div className="flex justify-center gap-2 px-5 py-3 bg-slate-50 rounded-b-2xl">
					{buttonList.map((btn) => {
						if (btn === "cancel") {
							return (
								<button
									key={btn}
									type="button"
									ref={cancelRef}
									onClick={() => handleAction("cancel")}
									className="
										inline-flex items-center justify-center
										rounded-full border border-slate-300 bg-white
										px-5 py-1.5 text-sm font-medium text-slate-700
										hover:bg-slate-100
										active:scale-[0.98]
										transition
										focus:outline-none
										focus:ring-2 focus:ring-slate-400
										focus:ring-offset-2 focus:ring-offset-slate-50
									"
								>
									Cancelar
								</button>
							);
						}

						if (btn === "ok") {
							return (
								<button
									key={btn}
									type="button"
									ref={okRef}
									onClick={() => handleAction("ok")}
									className="
										inline-flex items-center justify-center
										rounded-full bg-sky-600
										px-5 py-1.5 text-sm font-semibold text-white
										hover:bg-sky-700
										active:scale-[0.98]
										shadow-sm
										transition
										focus:outline-none
										focus:ring-2 focus:ring-slate-400
										focus:ring-offset-2 focus:ring-offset-slate-50
									"
								>
									OK
								</button>
							);
						}

						if (btn === "confirm") {
							return (
								<button
									key={btn}
									type="button"
									ref={confirmRef}
									onClick={() => handleAction("confirm")}
									// 									className="
									// 	inline-flex items-center justify-center
									// 	rounded-full bg-emerald-600
									// 	px-5 py-1.5 text-sm font-semibold text-white
									// 	hover:bg-emerald-700
									// 	active:scale-[0.98]
									// 	shadow-sm
									// 	transition
									// 	focus:outline-none
									// 	focus:bg-red-600
									// 	focus:hover:bg-red-700
									// 	focus:ring-2 focus:ring-red-500
									// 	focus:ring-offset-2 focus:ring-offset-slate-50
									// "
									className="
									inline-flex items-center justify-center rounded-full bg-emerald-600	px-5 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 active:scale-[0.98] transition focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50"
								>
									Confirmar
								</button>
							);
						}

						return null;
					})}
				</div>
			</div>
		</Modal>
	);
}
